import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { UserCellWithTooltip } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { useGetStreamUsers } from "@/modules/chat/hooks/useGetStreamUsers"
import { GetStreamUserEntity } from "@modules/chat/entities/getStreamUserEntity"
import { word } from "@modules/core/utils/i18n"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useCallback, useEffect, useRef } from "react"

type UserSearchListProps = {
  isCreatingGroup: boolean
  selectedUsers: GetStreamUserEntity[]
  onUserSelect: (user: GetStreamUserEntity) => void
  onUserClick?: (userId: string) => void
  searchTerm: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<GetStreamUserEntity>()

export function UserSearchList({
  isCreatingGroup,
  selectedUsers,
  onUserSelect,
  onUserClick,
  searchTerm,
}: UserSearchListProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const {
    data: { content: users },
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetStreamUsers(searchTerm)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const currentRef = loadMoreRef.current

    if (!scrollContainer || !currentRef) return

    const observer = new IntersectionObserver(handleIntersection, {
      root: scrollContainer,
      rootMargin: "100px",
      threshold: 0.1,
    })

    observer.observe(currentRef)

    let innerRafId: number | undefined
    const rafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        const isNotScrollable = scrollContainer.scrollHeight <= scrollContainer.clientHeight
        const containerRect = scrollContainer.getBoundingClientRect()
        const elementRect = currentRef.getBoundingClientRect()
        const isVisibleInViewport = elementRect.top < containerRect.bottom && elementRect.bottom > containerRect.top

        if ((isNotScrollable || isVisibleInViewport) && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      })
    })

    return () => {
      cancelAnimationFrame(rafId)
      if (innerRafId !== undefined) cancelAnimationFrame(innerRafId)
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage, users])

  const columns = [
    columnHelper.display({
      id: "checkbox",
      header: "",
      cell: ({ row: { original: user } }) =>
        isCreatingGroup ? (
          <Checkbox
            name="option"
            value={user.id}
            checked={!!selectedUsers.find((selectedUser) => selectedUser.id === user.id)}
            className="size-5 cursor-pointer hover:border-purple-400 focus-visible:ring-0 data-[state=checked]:border-purple-400 data-[state=checked]:bg-purple-500 data-[state=checked]:text-white"
          />
        ) : null,
      meta: {
        style: { width: 60, maxWidth: 60, height: 54 },
      },
    }),
    columnHelper.accessor("username", {
      header: word("chat.name"),
      cell: ({ row: { original: user } }) => (
        <UserCellWithTooltip
          tooltipContentProps={{ className: "max-w-35" }}
          onClick={() => onUserClick?.(user.id)}
          className={onUserClick ? "cursor-pointer" : undefined}
          member={{
            avatar: user.avatar,
            firstname: user.firstname,
            lastname: user.lastname,
            username: user.username,
          }}
          subtitle={
            <Text fontStyle="LS" className="truncate text-neutral-600">
              @{user.username}
            </Text>
          }
          isOnline={user.online}
        />
      ),
      meta: {
        style: { width: 300, maxWidth: 300, height: 54 },
      },
    }),
    columnHelper.accessor("last_active", {
      header: word("chat.last_online_label"),
      cell: ({ row: { original: user } }) => dayjs(user.last_active).calendar(),
      meta: {
        style: { width: 200, maxWidth: 200, height: 54 },
      },
    }),
  ]

  return (
    <>
      {searchTerm.length > 0 && users.length > 0 ? (
        <div ref={scrollContainerRef} className="flex max-h-full flex-col overflow-y-auto">
          <HUDatatable
            data={users}
            columns={columns}
            handleRowSelectionChange={(user) => (isCreatingGroup ? onUserSelect(user) : onUserClick?.(user.id))}
            hideFooter
          />
          {(hasNextPage || isFetchingNextPage) && (
            <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
              <Text fontStyle="BM">{word("global.loading")}</Text>
            </div>
          )}
        </div>
      ) : searchTerm.length === 0 ? (
        <Text fontStyle="LL" className="m-auto text-neutral-800">
          {word("chat.type_name")}
        </Text>
      ) : searchTerm.length > 0 && users.length === 0 ? (
        <Text fontStyle="LL" className="m-auto text-neutral-800">
          {word("chat.no_result.title")}
        </Text>
      ) : null}
    </>
  )
}
